import React, { useCallback, useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

interface TimePickerProps {
  value?: string; // HH:MM format
  onChange?: (time: string) => void;
  error?: string;
  label?: string;
  className?: string;
  min?: string; // HH:MM format
  max?: string; // HH:MM format
  required?: boolean;
  useScrollPicker?: boolean; // New prop to enable scroll picker
  disabled?: boolean;
}

const TimePicker: React.FC<TimePickerProps> = ({
  value = "",
  onChange,
  error,
  label,
  className = "",
  min = "00:00",
  max = "23:59",
  required = false,
  useScrollPicker = false,
  disabled = false,
}) => {
  const { t } = useTranslation();
  const resolvedLabel = label ?? t("sessionForm.selectTime");
  const [showScrollPicker, setShowScrollPicker] = useState(false);
  const [selectedHour, setSelectedHour] = useState(12); // 12 AM represents 00:00 in 24h format
  const [selectedMinute, setSelectedMinute] = useState(0);
  const [isAM, setIsAM] = useState(true); // 12:00 AM = 00:00
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Convert 24h format to 12h format for display
  const convertTo12Hour = (hour24: number) => {
    if (hour24 === 0) return 12;
    if (hour24 > 12) return hour24 - 12;
    return hour24;
  };

  // Convert 12h format to 24h format
  const convertTo24Hour = (hour12: number, isAM: boolean) => {
    if (hour12 === 12) {
      return isAM ? 0 : 12;
    }
    return isAM ? hour12 : hour12 + 12;
  };

  // Parse initial value for scroll picker
  useEffect(() => {
    if (value && value.includes(":")) {
      const [h, m] = value.split(":").map(Number);
      setSelectedHour(convertTo12Hour(h));
      setSelectedMinute(m);
      setIsAM(h < 12);
    }
    // Removed automatic default time setting - let user choose their own time
  }, [value]);

  // Handle native time input change
  const handleTimeChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const timeValue = e.target.value;
      onChange?.(timeValue);
    },
    [onChange]
  );

  // Handle scroll picker time change
  const handleScrollPickerChange = useCallback(
    (hour: number, minute: number, ampm: boolean) => {
      const hour24 = convertTo24Hour(hour, ampm);
      const timeString = `${hour24.toString().padStart(2, "0")}:${minute
        .toString()
        .padStart(2, "0")}`;
      onChange?.(timeString);
    },
    [onChange]
  );

  // Generate hours (1-12)
  const hours = Array.from({ length: 12 }, (_, i) => i + 1);

  // Generate minutes (0-59, step by 5)
  const minutes = Array.from({ length: 12 }, (_, i) => i * 5);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
      const target = event.target as Node;
      if (
        showScrollPicker &&
        dropdownRef.current &&
        target &&
        !dropdownRef.current.contains(target)
      ) {
        setShowScrollPicker(false);
      }
    };

    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === "Escape" && showScrollPicker) {
        setShowScrollPicker(false);
      }
    };

    if (showScrollPicker) {
      // Add slight delay to prevent immediate closing when opening
      setTimeout(() => {
        document.addEventListener("mousedown", handleClickOutside);
        document.addEventListener("touchstart", handleClickOutside);
        document.addEventListener("keydown", handleEscapeKey);
      }, 100);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
      document.removeEventListener("keydown", handleEscapeKey);
    };
  }, [showScrollPicker]);

  const handleHourSelect = (hour: number) => {
    setSelectedHour(hour);
    handleScrollPickerChange(hour, selectedMinute, isAM);
  };

  const handleMinuteSelect = (minute: number) => {
    setSelectedMinute(minute);
    handleScrollPickerChange(selectedHour, minute, isAM);
  };

  const handleAMPMToggle = (ampm: boolean) => {
    setIsAM(ampm);
    handleScrollPickerChange(selectedHour, selectedMinute, ampm);
  };

  if (useScrollPicker) {
    return (
      <div className={`space-y-1 ${className}`}>
        {resolvedLabel && (
          <label className="block text-sm font-bold text-grey-30 dark:text-grey-90 mb-1">
            {resolvedLabel}:
          </label>
        )}

        <div className="relative max-w-[8rem]" ref={dropdownRef}>
          {/* Clock Icon */}
          <div className="absolute inset-y-0 right-0 top-0 flex items-center pr-3.5 pointer-events-none z-10">
            <svg
              className="w-4 h-4 text-grey-50 dark:text-grey-70"
              aria-hidden="true"
              xmlns="http://www.w3.org/2000/svg"
              fill="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                fillRule="evenodd"
                d="M2 12C2 6.477 6.477 2 12 2s10 4.477 10 10-4.477 10-10 10S2 17.523 2 12Zm11-4a1 1 0 1 0-2 0v4a1 1 0 0 0 .293.707l3 3a1 1 0 0 0 1.414-1.414L13 11.586V8Z"
                clipRule="evenodd"
              />
            </svg>
          </div>

          {/* Time Display Button */}
          <button
            type="button"
            onClick={() => {
              if (!disabled) {
                // Just toggle the picker - don't automatically set a default time
                setShowScrollPicker(!showScrollPicker);
              }
            }}
            disabled={disabled}
            className={`
              w-full
              bg-white dark:bg-primary-10
              text-grey-20 dark:text-white
              text-sm
              rounded-lg
              leading-none
              block
              p-4
              pr-10
              text-left
              outline-primary-40
              dark:outline-grey-90
              ${
                showScrollPicker && value
                  ? "border-transparent outline-2 dark:outline-1 "
                  : "border border-grey-70 dark:border-grey-50"
              }
              ${
                error
                  ? "border-error focus:border-error focus:ring-red-200 dark:focus:ring-red-200"
                  : "focus:border-primary-40 dark:focus:border-grey-90 focus-visible:border-primary-40 dark:focus-visible:border-grey-90"
              }
              ${error ? "bg-red-50 dark:bg-red-900/20" : ""}
            `}
          >
            {value
              ? (() => {
                  const [h, m] = value.split(":").map(Number);
                  const hour12 = convertTo12Hour(h);
                  const ampm = h < 12 ? "AM" : "PM";
                  return `${hour12.toString().padStart(2, "0")}:${m
                    .toString()
                    .padStart(2, "0")} ${ampm}`;
                })()
              : "12:00 AM"}
          </button>

          {/* Scroll Picker Dropdown */}
          {showScrollPicker && (
            <div className="absolute top-full left-0 mt-1 bg-white dark:bg-primary-10 border border-grey-70 dark:border-grey-50 rounded-lg shadow-lg z-[90] p-4 min-w-[280px]">
              <div className="flex gap-3 items-start">
                {/* Hours Scroll */}
                <div className="flex-1">
                  <label className="block text-xs font-medium text-grey-30 dark:text-grey-70 mb-2 text-center">
                    {t("sessionForm.hour")}
                  </label>
                  <div className="max-h-32 overflow-y-auto border border-grey-70 dark:border-grey-50 rounded-lg bg-white dark:bg-primary-10">
                    {hours.map((hour) => (
                      <button
                        key={hour}
                        type="button"
                        onClick={() => handleHourSelect(hour)}
                        className={`
                          w-full px-3 py-2 text-sm text-center transition-colors border-b border-grey-70 dark:border-grey-50 last:border-b-0
                          ${
                            selectedHour === hour
                              ? "bg-primary-40 text-white dark:bg-primary-90 dark:text-primary-20"
                              : "text-grey-20 dark:text-white bg-white dark:bg-primary-10 hover:bg-primary-95 dark:hover:bg-[color-mix(in_oklab,var(--color-primary-90)_10%,transparent)]"
                          }
                        `}
                      >
                        {hour.toString().padStart(2, "0")}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Minutes Scroll */}
                <div className="flex-1">
                  <label className="block text-xs font-medium text-grey-30 dark:text-grey-70 mb-2 text-center">
                    {t("sessionForm.min")}
                  </label>
                  <div className="max-h-32 overflow-y-auto border border-grey-70 dark:border-grey-50 rounded-lg bg-white dark:bg-primary-10">
                    {minutes.map((minute) => (
                      <button
                        key={minute}
                        type="button"
                        onClick={() => handleMinuteSelect(minute)}
                        className={`
                          w-full px-3 py-2 text-sm text-center transition-colors border-b border-grey-70 dark:border-grey-50 last:border-b-0
                          ${
                            selectedMinute === minute
                              ? "bg-primary-40 text-white dark:bg-primary-90 dark:text-primary-20"
                              : "text-grey-20 dark:text-white bg-white dark:bg-primary-10 hover:bg-primary-95 dark:hover:bg-[color-mix(in_oklab,var(--color-primary-90)_10%,transparent)]"
                          }
                        `}
                      >
                        {minute.toString().padStart(2, "0")}
                      </button>
                    ))}
                  </div>
                </div>

                {/* AM/PM Toggle */}
                <div className="flex-1">
                  <label className="block text-xs font-medium text-grey-30 dark:text-grey-70 mb-2 text-center">
                    {t("sessionForm.period")}
                  </label>
                  <div className="space-y-2">
                    <button
                      type="button"
                      onClick={() => handleAMPMToggle(true)}
                      className={`
                        w-full px-3 py-3 text-sm rounded-lg transition-colors font-medium
                        ${
                          isAM
                            ? "bg-primary-40 text-white dark:bg-primary-90 dark:text-primary-20"
                            : "bg-white dark:bg-primary-10 text-grey-20 dark:text-white hover:bg-primary-95 dark:hover:bg-[color-mix(in_oklab,var(--color-primary-90)_10%,transparent)] border border-grey-70 dark:border-grey-50"
                        }
                      `}
                    >
                      AM
                    </button>
                    <button
                      type="button"
                      onClick={() => handleAMPMToggle(false)}
                      className={`
                        w-full px-3 py-3 text-sm rounded-lg transition-colors font-medium
                        ${
                          !isAM
                            ? "bg-primary-40 text-white dark:bg-primary-90 dark:text-primary-20"
                            : "bg-white dark:bg-primary-10 text-grey-20 dark:text-white hover:bg-primary-95 dark:hover:bg-[color-mix(in_oklab,var(--color-primary-90)_10%,transparent)] border border-grey-70 dark:border-grey-50"
                        }
                      `}
                    >
                      PM
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Error message */}
        {error && (
          <p className="field-error mt-1 text-xs">
            <svg
              width="12"
              height="12"
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className="flex-shrink-0"
            >
              <path
                d="M8 0C12.4183 0 16 3.58172 16 8C16 10.1217 15.1575 12.1569 13.6572 13.6572C12.1569 15.1575 10.1217 16 8 16C3.58172 16 0 12.4183 0 8C0 3.58172 3.58172 0 8 0ZM8.19043 11.0195C7.86419 10.9542 7.52644 11.0559 7.29004 11.29C7.10286 11.4788 6.99846 11.7342 7 12C6.99846 12.2658 7.10286 12.5212 7.29004 12.71C7.38724 12.7983 7.49893 12.8693 7.62012 12.9199C7.86212 13.0269 8.13788 13.0269 8.37988 12.9199C8.50106 12.8693 8.61276 12.7983 8.70996 12.71C8.89715 12.5212 9.00154 12.2658 9 12C8.99632 11.7352 8.89269 11.4817 8.70996 11.29C8.66244 11.247 8.61195 11.2068 8.55957 11.1699C8.50392 11.1322 8.44348 11.1019 8.37988 11.0801C8.32046 11.0497 8.25652 11.0288 8.19043 11.0195ZM8 3C7.44772 3 7 3.33579 7 3.75V8.25C7 8.66421 7.44772 9 8 9C8.55229 9 9 8.66421 9 8.25V3.75C9 3.33579 8.55229 3 8 3Z"
                fill="currentColor"
              />
            </svg>
            {error}
          </p>
        )}
      </div>
    );
  }

  // Default native time picker
  return (
    <div className={`space-y-1 ${className}`}>
      {label && (
        <label className="block text-sm font-bold text-grey-30 dark:text-grey-90 mb-1">
          {label}:
        </label>
      )}

      <div className="relative max-w-[8rem]">
        {/* Clock Icon */}
        <div className="absolute inset-y-0 right-0 top-0 flex items-center pr-3.5 pointer-events-none">
          <svg
            className="w-4 h-4 text-grey-50 dark:text-grey-70"
            aria-hidden="true"
            xmlns="http://www.w3.org/2000/svg"
            fill="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              fillRule="evenodd"
              d="M2 12C2 6.477 6.477 2 12 2s10 4.477 10 10-4.477 10-10 10S2 17.523 2 12Zm11-4a1 1 0 1 0-2 0v4a1 1 0 0 0 .293.707l3 3a1 1 0 0 0 1.414-1.414L13 11.586V8Z"
              clipRule="evenodd"
            />
          </svg>
        </div>

        {/* Time Input */}
        <input
          type="time"
          id="time-picker"
          value={value}
          onChange={handleTimeChange}
          min={min}
          max={max}
          required={required}
          disabled={disabled}
          className={`
            w-full
            bg-white dark:bg-primary-10
            border border-grey-20 dark:border-grey-70
            text-grey-20 dark:text-white
            text-sm
            rounded-lg
            leading-none
            block
            p-4
            pr-10
            transition-all
            focus:ring-2
            focus:ring-primary-40 dark:focus:ring-primary-60
            focus:border-primary-40 dark:focus:border-primary-60
            focus:outline-none
            hover:border-primary-40 dark:hover:border-white
            [&::-webkit-calendar-picker-indicator]:hidden
            ${
              error
                ? "border-error focus:border-error focus:ring-red-200 dark:focus:ring-red-200"
                : ""
            }
            ${error ? "bg-red-50 dark:bg-red-900/20" : ""}
          `}
        />
      </div>

      {/* Error message */}
      {error && (
        <p className="field-error mt-1 text-xs">
          <svg
            width="12"
            height="12"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className="flex-shrink-0"
          >
            <path
              d="M8 0C12.4183 0 16 3.58172 16 8C16 10.1217 15.1575 12.1569 13.6572 13.6572C12.1569 15.1575 10.1217 16 8 16C3.58172 16 0 12.4183 0 8C0 3.58172 3.58172 0 8 0ZM8.19043 11.0195C7.86419 10.9542 7.52644 11.0559 7.29004 11.29C7.10286 11.4788 6.99846 11.7342 7 12C6.99846 12.2658 7.10286 12.5212 7.29004 12.71C7.38724 12.7983 7.49893 12.8693 7.62012 12.9199C7.86212 13.0269 8.13788 13.0269 8.37988 12.9199C8.50106 12.8693 8.61276 12.7983 8.70996 12.71C8.89715 12.5212 9.00154 12.2658 9 12C8.99632 11.7352 8.89269 11.4817 8.70996 11.29C8.66244 11.247 8.61195 11.2068 8.55957 11.1699C8.50392 11.1322 8.44348 11.1019 8.37988 11.0801C8.32046 11.0497 8.25652 11.0288 8.19043 11.0195ZM8 3C7.44772 3 7 3.33579 7 3.75V8.25C7 8.66421 7.44772 9 8 9C8.55229 9 9 8.66421 9 8.25V3.75C9 3.33579 8.55229 3 8 3Z"
              fill="currentColor"
            />
          </svg>
          {error}
        </p>
      )}
    </div>
  );
};

export default TimePicker;
